import * as Sentry from '@sentry/nextjs';
import { type UpstreamRequestError, type UpstreamErrorSource } from '@koala/sdk/v4';
import { NonJsonResponseError } from '@/utils/networkErrors';

export interface NonJsonErrorContext {
  /** Route or page where the failure happened, e.g. `/store/[...id]`. */
  route?: string;
  /** Hint about the endpoint that returned non-JSON, e.g. `/menus`. */
  urlHint?: string;
  /** Current basket id, when available — helps tie events to a diner session. */
  basketId?: string;
  /** Current location id, when available. */
  locationId?: string;
  /** Attempt number when the error was captured (1-based). */
  attempt?: number;
  /** Which subsystem originated the call — populates the `error.source` Sentry tag. */
  source?: UpstreamErrorSource;
}

/**
 * Capture a structured Sentry event for a non-JSON response from coo-api.
 * The shape is modelled on {@link logToastPaymentError} so events are
 * filterable in Discover via consistent top-level tags.
 *
 * No `window` guard: the primary callers are `getServerSideProps` reads
 * (via {@link withJsonGuard}), which only ever run server-side. `@sentry/nextjs`
 * captures on the Node/edge runtimes (init happens in `instrumentation.ts`), so
 * dropping the guard is what lets the route/urlHint/locationId tags actually
 * reach Sentry instead of being discarded into a server log.
 */
export function reportNonJsonError(
  error: NonJsonResponseError | Error,
  ctx: NonJsonErrorContext = {},
): void {
  const status = error instanceof NonJsonResponseError ? error.status : undefined;
  const url = error instanceof NonJsonResponseError ? error.url : ctx.urlHint;
  const contentType = error instanceof NonJsonResponseError ? error.contentType : undefined;
  const bodySnippet = error instanceof NonJsonResponseError ? error.bodySnippet : undefined;

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error_class', 'non_json_response');
    scope.setTag('coo_api', 'true');

    scope.setTag('error.source', ctx.source ?? 'api');
    if (ctx.route) scope.setTag('route', ctx.route);
    if (typeof status === 'number') scope.setTag('response_status', String(status));
    if (ctx.basketId) scope.setTag('basket_id', ctx.basketId);
    if (ctx.locationId) scope.setTag('location_id', ctx.locationId);
    if (typeof ctx.attempt === 'number') scope.setTag('attempt', String(ctx.attempt));

    scope.setContext('non_json_response', {
      request_url: url,
      response_status: status,
      content_type: contentType,
      body_snippet: bodySnippet,
      attempt: ctx.attempt,
    });

    Sentry.captureException(error);
  });
}

/**
 * Capture a structured Sentry event for an {@link UpstreamRequestError},
 * mirroring the shape {@link reportNonJsonError} uses for non-JSON responses.
 * TEAM-12894.
 */
export function reportUpstreamError(
  error: UpstreamRequestError,
  ctx: NonJsonErrorContext = {},
): void {
  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error_class', 'upstream_request_error');
    scope.setTag('error.source', error.source);
    scope.setTag('coo_api', 'true');

    if (ctx.route) scope.setTag('route', ctx.route);
    if (typeof error.httpStatus === 'number')
      scope.setTag('response_status', String(error.httpStatus));
    if (error.requestId) scope.setTag('request_id', error.requestId);
    if (ctx.basketId) scope.setTag('basket_id', ctx.basketId);
    if (ctx.locationId) scope.setTag('location_id', ctx.locationId);
    if (typeof ctx.attempt === 'number') scope.setTag('attempt', String(ctx.attempt));
    if (typeof error.originalBodySize === 'number') {
      scope.setTag('original_body_size', String(error.originalBodySize));
    }

    scope.setContext('upstream_request', {
      request_url: error.url,
      response_status: error.httpStatus,
      body_excerpt: error.bodyExcerpt,
      request_id: error.requestId,
      correlation_id: error.correlationId,
      attempt: ctx.attempt,
    });

    Sentry.captureException(error);
  });
}

/**
 * Capture a Sentry event for a browser transport failure — a `fetch` that never got an HTTP
 * response (Safari's "Load failed", Chrome's "Failed to fetch", undici's "fetch failed"). These
 * are a diner's flaky connection, a backgrounded tab or a mid-load navigation; the request never
 * reached coo-api, so they are NOT an upstream outage and must not be counted as one. TEAM-15165.
 *
 * Distinct from {@link reportUpstreamError} in three ways:
 * - `warning` level, not `error`, so it stays out of the error-level coo-api dashboards/alerts.
 * - its own `error_class: network_failure` (and `coo_api: false`) so it is filterable apart from a
 *   real 5xx `upstream_request_error`.
 * - the original error (a bare `TypeError`) is captured as-is — unwrapped — so the retry predicates
 *   (`isNetworkError` / `isRetryableError` / `isTransportFailure`) and the connection-recovery UI
 *   keep seeing a network error.
 */
export function reportNetworkError(error: unknown, ctx: NonJsonErrorContext = {}): void {
  const online = typeof window !== 'undefined' ? window.navigator.onLine : undefined;

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('error_class', 'network_failure');
    scope.setTag('error.source', ctx.source ?? 'api');
    // The request never reached coo-api — keep it out of the coo_api:true outage dashboards.
    scope.setTag('coo_api', 'false');

    if (ctx.route) scope.setTag('route', ctx.route);
    if (ctx.basketId) scope.setTag('basket_id', ctx.basketId);
    if (ctx.locationId) scope.setTag('location_id', ctx.locationId);
    if (typeof ctx.attempt === 'number') scope.setTag('attempt', String(ctx.attempt));
    if (typeof online === 'boolean') scope.setTag('online', String(online));

    scope.setContext('network_failure', {
      request_url: ctx.urlHint,
      attempt: ctx.attempt,
      online,
    });

    Sentry.captureException(error);
  });
}
